import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, ToggleButtonGroup, ToggleButton, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Alert, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import {
  SalonMember, TaskCategory, TaskStats, taskApi, taskErrorMessage,
} from '../../services/taskService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { SectionCard as StatsCard, EmptyHint, StatTileGrid, StatsRow } from '../salon/stats/statsShared';
import { StatsDateRange, DateRange, RangePreset, computePresetRange } from '../salon/stats/StatsDateRange';
import { GroupedBarChart, DonutChart } from '../salon/stats/ChartKit';
import {
  PRIORITY_COLOR, PRIORITY_LABEL, STATUS_COLOR, STATUS_LABEL, formatDuration, userLabel,
} from './taskShared';
import { TaskPriority, TaskStatus } from '../../services/taskService';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, PersonAvatar,
} from '../../components/ui';
import { tokens, CHART_PALETTE } from '../../theme';
import { countLabel, formatDayMonthShort, formatNumber } from '../../utils/format';

/** Srednie w godzinach (ulamki) jako czytelny czas: „2 h 15 min”, „36 min”. */
const hours = (v: number | null | undefined): string =>
  (v === null || v === undefined ? '—' : formatDuration(v * 3600));

const monthShort = new Intl.DateTimeFormat('pl-PL', { month: 'short', year: 'numeric' });

/** Klucz przedzialu z API („2026-09” albo „2026-09-07”) jako etykieta osi. */
const bucketLabel = (bucket: string): string => {
  const month = /^(\d{4})-(\d{2})$/.exec(bucket);
  if (month) return monthShort.format(new Date(Number(month[1]), Number(month[2]) - 1, 1));
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket);
  if (day) return formatDayMonthShort(new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])));
  return bucket;
};

/** Polskie nazwy wskaznikow z opisu API (klucze sa techniczne). */
const DEFINITION_LABEL: Record<string, string> = {
  onTimePercent: 'Zakończone w terminie',
  avgCompletionHours: 'Średni czas realizacji',
  avgTimeToStartHours: 'Od utworzenia do startu',
  avgApprovalWaitHours: 'Oczekiwanie na akceptację',
  estimateVsActual: 'Czas szacowany a rzeczywisty',
  trackedSeconds: 'Zarejestrowany czas',
  workload: 'Obciążenie pracowników',
  reworkCount: 'Zwroty do poprawy',
};

const tasksGenitive = (n: number) => countLabel(n, 'zadania', 'zadań', 'zadań');

/** Etykieta kafelka z ikonka objasnienia (dotkniecie pokazuje opis takze na telefonie). */
const HintLabel: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
  <Box component="span">
    {label}
    {hint && (
      <Tooltip title={hint} enterTouchDelay={0} leaveTouchDelay={4000}>
        <Box
          component="span"
          tabIndex={0}
          role="img"
          aria-label={`Objaśnienie: ${hint}`}
          sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 0.5, mt: '-2px', color: tokens.subtle, cursor: 'help', borderRadius: '50%', '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 1 } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    )}
  </Box>
);

const CountRow: React.FC<{ color: string; label: string; count: number }> = ({ color, label, count }) => (
  <Stack direction="row" alignItems="center" gap={1.25} sx={{ py: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft, flex: 1, minWidth: 0 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(count)}</Typography>
  </Stack>
);

export const TaskStatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeSalonId, salons } = useSalonContextStore();
  const salonId = activeSalonId || salons[0]?.id || '';

  const [scope, setScope] = useState<'salon' | 'me'>('me');
  const [isManager, setIsManager] = useState(false);
  const [preset, setPreset] = useState<RangePreset>('month');
  const [range, setRange] = useState<DateRange>(() => computePresetRange('month'));
  const [assigneeId, setAssigneeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [members, setMembers] = useState<SalonMember[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!salonId) return;
    Promise.all([taskApi.context(), taskApi.members(salonId), taskApi.listCategories(salonId)])
      .then(([ctx, mem, cats]) => {
        setIsManager(ctx.isManager);
        setScope(ctx.isManager ? 'salon' : 'me');
        setMembers(mem);
        setCategories(cats);
      })
      .catch(() => setError('Nie udało się załadować danych salonu. Odśwież stronę, aby spróbować ponownie.'));
  }, [salonId]);

  const load = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        salonId,
        from: range.dateFrom,
        to: range.dateTo,
        granularity: preset === 'year' ? 'month' : preset === 'day' ? 'day' : 'week',
        ...(categoryId ? { categoryId } : {}),
        ...(scope === 'salon' && assigneeId ? { assigneeId } : {}),
      };
      setStats(scope === 'salon' ? await taskApi.salonStats(params) : await taskApi.myStats(params));
    } catch (err) {
      setError(taskErrorMessage(err, 'Nie udało się załadować statystyk.'));
    } finally {
      setLoading(false);
    }
  }, [salonId, scope, range, preset, assigneeId, categoryId]);

  useEffect(() => { load(); }, [load]);

  const statusData = useMemo(
    () => (stats?.byStatus || [])
      .filter((s) => s.count > 0)
      .map((s) => ({
        name: STATUS_LABEL[s.status as TaskStatus],
        value: s.count,
        color: STATUS_COLOR[s.status as TaskStatus].dot,
      })),
    [stats],
  );

  const trendData = useMemo(
    () => (stats?.trend || []).map((t) => ({ label: bucketLabel(t.bucket), created: t.created, completed: t.completed })),
    [stats],
  );

  const estimateData = useMemo(() => {
    if (!stats) return [];
    return [{
      label: 'Zakończone zadania',
      szacowany: Math.round(stats.estimateVsActual.estimatedMinutes / 60 * 10) / 10,
      rzeczywisty: Math.round(stats.estimateVsActual.actualMinutes / 60 * 10) / 10,
    }];
  }, [stats]);

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Analizy' }]}
      title="Statystyki zadań"
      description="Dane rzeczywiste — służą do planowania pracy, nie do automatycznej oceny pracowników."
      actions={(
        <>
          {isManager && (
            <ToggleButtonGroup
              exclusive value={scope}
              onChange={(_, v) => v && setScope(v)}
              aria-label="Zakres statystyk"
            >
              <ToggleButton value="salon">Cały salon</ToggleButton>
              <ToggleButton value="me">Moje zadania</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')}>
            Lista zadań
          </Button>
        </>
      )}
    />
  );

  if (!salonId) {
    return (
      <Box>
        {header}
        <SectionCard>
          <EmptyState
            icon={<InsightsOutlinedIcon />}
            title="Wybierz salon"
            description="Statystyki zadań liczymy dla jednego salonu. Wybierz go w menu po lewej stronie."
          />
        </SectionCard>
      </Box>
    );
  }

  const s = stats?.summary;
  const hasPriorities = !!stats && stats.byPriority.some((p) => p.count > 0);

  return (
    <Box>
      {header}

      <StatsDateRange
        value={range}
        preset={preset}
        onChange={(r, p) => { setRange(r); setPreset(p); }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mt: -1, mb: 3 }}>
        {isManager && scope === 'salon' && (
          <TextField
            select size="small" label="Pracownik" value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: { sm: 240 } }}
          >
            <MenuItem value="">Wszyscy pracownicy</MenuItem>
            {members.map((m) => (
              <MenuItem key={m.userId} value={m.userId}>{userLabel({ id: m.userId, ...m })}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select size="small" label="Kategoria" value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: { sm: 220 } }}
        >
          <MenuItem value="">Wszystkie kategorie</MenuItem>
          {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
      {loading && !stats && <LoadingState variant="page" />}

      {stats && s && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s ease' }} aria-busy={loading || undefined}>
          <StatTileGrid columns={4}>
            <StatTile tone="hero" label="Zadania otwarte" value={formatNumber(s.openTasks)} hint="stan na teraz" />
            <StatTile
              label="Przeterminowane"
              value={formatNumber(s.overdue)}
              valueColor={s.overdue > 0 ? tokens.danger : undefined}
              hint={s.overdue > 0 ? 'otwarte zadania po terminie' : 'nic nie jest po terminie'}
            />
            <StatTile
              label="Zakończone w okresie"
              value={formatNumber(s.completedInRange)}
              hint={`utworzono w okresie: ${formatNumber(s.createdInRange)}`}
            />
            <StatTile
              label={<HintLabel label="Zakończone w terminie" hint={stats.definitions.onTimePercent} />}
              value={s.onTime.percent === null ? '—' : formatNumber(s.onTime.percent)}
              unit={s.onTime.percent === null ? undefined : '%'}
              progress={s.onTime.percent ?? undefined}
              hint={s.onTime.completedWithDueDate > 0
                ? `${formatNumber(s.onTime.onTime)} z ${tasksGenitive(s.onTime.completedWithDueDate)} z terminem`
                : 'brak zakończonych zadań z terminem'}
            />
          </StatTileGrid>

          <StatTileGrid columns={4} sx={{ mt: 2 }}>
            <StatTile
              label={<HintLabel label="Do weryfikacji" hint="Zadania przekazane przez pracowników, czekające na sprawdzenie." />}
              value={formatNumber(s.pendingReview)}
            />
            <StatTile
              label={<HintLabel label="Do akceptacji" hint="Zadania czekające na formalną akceptację właściciela." />}
              value={formatNumber(s.pendingApproval)}
            />
            <StatTile
              label={<HintLabel label="Zwroty do poprawy" hint={stats.definitions.reworkCount} />}
              value={formatNumber(s.reworkCount)}
            />
            <StatTile
              label={<HintLabel label="Zarejestrowany czas" hint={stats.definitions.trackedSeconds} />}
              value={formatDuration(s.trackedSeconds)}
              hint={countLabel(s.trackedSessions, 'sesja pomiaru', 'sesje pomiaru', 'sesji pomiaru')}
            />
            <StatTile
              label={<HintLabel label="Średni czas realizacji" hint={stats.definitions.avgCompletionHours} />}
              value={hours(s.avgCompletionHours)}
            />
            <StatTile
              label={<HintLabel label="Od utworzenia do startu" hint={stats.definitions.avgTimeToStartHours} />}
              value={hours(s.avgTimeToStartHours)}
              hint={`na podstawie ${tasksGenitive(s.timeToStartSampleSize)}`}
            />
            <StatTile
              label={<HintLabel label="Oczekiwanie na akceptację" hint={stats.definitions.avgApprovalWaitHours} />}
              value={hours(s.avgApprovalWaitHours)}
              hint={`na podstawie ${tasksGenitive(s.approvalWaitSampleSize)}`}
            />
            <StatTile
              label={(
                <HintLabel
                  label="Zakończone bez terminu"
                  hint="Zadania zakończone w okresie, które nie miały ustawionego terminu — nie wliczają się do wskaźnika terminowości."
                />
              )}
              value={formatNumber(s.onTime.withoutDueDate)}
            />
          </StatTileGrid>

          <StatsRow template="1fr 1fr" sx={{ mt: 2.5 }}>
            <StatsCard title="Utworzone i zakończone w czasie">
              {trendData.length === 0 ? (
                <EmptyHint icon={<InsightsOutlinedIcon />} description="Zmień okres albo filtry, żeby zobaczyć trend.">
                  Brak danych w wybranym okresie.
                </EmptyHint>
              ) : (
                <GroupedBarChart
                  data={trendData}
                  xKey="label"
                  bars={[
                    { key: 'created', name: 'Utworzone', color: CHART_PALETTE[0] },
                    { key: 'completed', name: 'Zakończone', color: CHART_PALETTE[1] },
                  ]}
                />
              )}
            </StatsCard>
            <StatsCard title="Zadania według statusu">
              {statusData.length === 0 ? (
                <EmptyHint>Brak zadań dla wybranych filtrów.</EmptyHint>
              ) : (
                <DonutChart data={statusData} currency={false} />
              )}
            </StatsCard>
          </StatsRow>

          <StatsRow template="1fr 1fr" sx={{ mt: 2.5 }}>
            <StatsCard title="Czas szacowany a rzeczywisty" subtitle="W godzinach, dla zakończonych zadań z oszacowaniem">
              {stats.estimateVsActual.taskCount === 0 ? (
                <EmptyHint description="Podawaj szacowany czas przy tworzeniu zadań, żeby porównać go z rzeczywistym.">
                  Żadne zakończone zadanie w tym okresie nie miało szacowanego czasu.
                </EmptyHint>
              ) : (
                <>
                  <GroupedBarChart
                    data={estimateData}
                    xKey="label"
                    bars={[
                      { key: 'szacowany', name: 'Szacowany', color: CHART_PALETTE[3] },
                      { key: 'rzeczywisty', name: 'Rzeczywisty', color: CHART_PALETTE[0] },
                    ]}
                    height={220}
                  />
                  <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 1.25 }}>
                    Porównanie obejmuje {countLabel(stats.estimateVsActual.taskCount, 'zadanie', 'zadania', 'zadań')} z szacowanym czasem.
                    {stats.estimateVsActual.withoutEstimate > 0
                      && ` Pominięto ${countLabel(stats.estimateVsActual.withoutEstimate, 'zadanie', 'zadania', 'zadań')} bez szacowanego czasu.`}
                  </Typography>
                </>
              )}
            </StatsCard>
            <StatsCard title="Priorytety i kategorie">
              {!hasPriorities ? (
                <EmptyHint>Brak zadań dla wybranych filtrów.</EmptyHint>
              ) : (
                <Box sx={{ display: 'grid', gap: { xs: 2, sm: 3 }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 0.5 }}>Priorytet</Typography>
                    {stats.byPriority.filter((p) => p.count > 0).map((p) => (
                      <CountRow
                        key={p.priority}
                        color={PRIORITY_COLOR[p.priority as TaskPriority]}
                        label={PRIORITY_LABEL[p.priority as TaskPriority]}
                        count={p.count}
                      />
                    ))}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 0.5 }}>Kategoria</Typography>
                    {stats.byCategory.filter((c) => c.count > 0).map((c) => (
                      <CountRow key={c.categoryId || 'none'} color={c.color || tokens.subtle} label={c.name} count={c.count} />
                    ))}
                  </Box>
                </Box>
              )}
            </StatsCard>
          </StatsRow>

          {stats.byEmployee && (
            <SectionCard
              title="Obciążenie i realizacja według pracownika"
              subtitle={stats.definitions.workload}
              padding="none"
              sx={{ mt: 2.5 }}
            >
              {stats.byEmployee.length === 0 ? (
                <EmptyHint>Brak aktywności pracowników w tym okresie.</EmptyHint>
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Pracownik</TableCell>
                        <TableCell align="right">Otwarte</TableCell>
                        <TableCell align="right">Zakończone</TableCell>
                        <TableCell align="right">W terminie</TableCell>
                        <TableCell align="right">Śr. realizacja</TableCell>
                        <TableCell align="right">Zarejestrowany czas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.byEmployee.map((e) => (
                        <TableRow key={e.userId} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1.25}>
                              <PersonAvatar firstName={e.firstName} lastName={e.lastName} fallback={e.email} seed={e.userId} size={32} />
                              <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                                {userLabel({ id: e.userId, firstName: e.firstName, lastName: e.lastName, email: e.email })}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(e.openTasks)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(e.completedInRange)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {e.onTimePercent === null ? (
                              <Tooltip title="Brak zakończonych zadań z terminem">
                                <Box component="span" sx={{ color: tokens.muted }}>—</Box>
                              </Tooltip>
                            ) : (
                              <>
                                {e.onTimePercent}%
                                <Box component="span" sx={{ color: tokens.muted, ml: 0.75 }}>
                                  {e.onTime}/{e.completedWithDueDate}
                                </Box>
                              </>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{hours(e.avgCompletionHours)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(e.trackedSeconds)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}

          <Accordion sx={{ mt: 2.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" gap={1} alignItems="center">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: tokens.muted }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                  Jak liczymy te wskaźniki?
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pb: 2.5 }}>
              <Box
                component="dl"
                sx={{ m: 0, display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}
              >
                {Object.entries(stats.definitions).map(([key, text]) => (
                  <Box key={key}>
                    <Typography component="dt" sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
                      {DEFINITION_LABEL[key] || 'Wskaźnik'}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0, mt: 0.25, fontSize: '0.875rem', color: tokens.inkSoft, lineHeight: 1.55 }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default TaskStatsPage;
